import { IAirlineWebapiService } from "./airline-webapi.service.interface";
import { HttpVerb } from "../../types/http-verb";
import { buildFullUrl, isHttpErrorStatusCode } from "../http/http-helpers";
import { APP_VERSION } from "../../version";
import { IGetConfigurationsResponse } from "./responses/get-configurations.response";
import { ServiceBase } from "../service-base";

import { AirlineWebapiResponse } from "./airline-webapi-response";
import { AirlineWebapiErrorCodesEnum } from "./airline-webapi-error-codes.enum";
import {DeeplinkAgentTokenRequest} from "./requests/deeplink-agent-token.request";
import {DeeplinkAgentTokenResponse} from "./responses/deeplink-agent-token.response";
import {ICreateDotRezAnonymousTokenResponse} from "./responses/create-dotrez-anonymous-token.response";

interface IFetchOptions {
    pathName: string;
    body?: any;
    doNotThrowOnError?: boolean;
}

export class AirlineWebapiService extends ServiceBase implements IAirlineWebapiService {

    private buildAirlineWebApiUrl(path: string): string {
        return buildFullUrl(this.services.configuration.airlineWebapiUrl, path);
    }

    private async _fetchData<TData = any>(method: HttpVerb, options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {

        const url = this.buildAirlineWebApiUrl(options.pathName);
        const fetchResponse = await fetch(url, {
            method: method,
            body: options.body && JSON.stringify(options.body),
            headers: {
                'Content-Type': 'application/json'
            }
        });

        let fetchResponseData = await fetchResponse.json();

        if (isHttpErrorStatusCode(fetchResponse.status) || fetchResponseData.errorCode !== AirlineWebapiErrorCodesEnum.Success) {
            if (options.doNotThrowOnError) {
                return new AirlineWebapiResponse<TData>(fetchResponseData.errorCode || AirlineWebapiErrorCodesEnum.GeneralError, fetchResponseData.data);
            } else {
                throw new Error(`Failed to fetch from ${url}`);
            }
        }

        return new AirlineWebapiResponse<TData>(fetchResponseData.errorCode, fetchResponseData.data);
    }


    private async get<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('GET', options);
    }

    private async post<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('POST', options);
    }

    private async patch<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('PATCH', options);
    }

    private async put<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('PUT', options);
    }

    private async delete<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('DELETE', options);
    }

    async getConfigurations(language: string): Promise<IGetConfigurationsResponse> {

        const response = await this.post<IGetConfigurationsResponse>({
            pathName: '/api/ClientConfigurations/v2/searchMask', body: {
                language: language,
                clientVersion: APP_VERSION,
                deviceType: 1
            }
        });

        return response.data;
    }

    async createDotRezAnonymousToken(): Promise<ICreateDotRezAnonymousTokenResponse> {
        const response = await this.post<ICreateDotRezAnonymousTokenResponse>({
            pathName: '/api/user/v1/website/anonymousToken',
            body:{}
        });

        return response.data;
    }

    async getDeepLinkAgentToken(request: DeeplinkAgentTokenRequest): Promise<DeeplinkAgentTokenResponse> {
        const response = await this.post<DeeplinkAgentTokenResponse>({
            pathName: '/api/User/v2/deepLinkAgentToken', body: {
                agentName: request.agentName
            }
        });

        return response.data;
    }
}
